import {Combobox} from '@headlessui/react';
import usePlacesAutocomplete, {GeocodeResult, getGeocode, getLatLng} from 'use-places-autocomplete';
import {ReactComponent as CrossIcon} from '../../../../../../assets/images/icons/cross.svg';
import {ReactComponent as SearchIcon} from '../../../../../../assets/images/icons/search.svg';
import {useI18n} from '../../../../../common/providers/I18nProvider';
import {SelectedLocation} from '../Types';

const buildLocationFromResult = (result: GeocodeResult): SelectedLocation => {
	const latLng = getLatLng(result);
	let zipCode;
	let building;
	let streetNo;
	let prefecture;
	result.address_components.forEach((c) => {
		if (c.types.includes('postal_code')) zipCode = c.long_name;
		if (c.types.includes('subpremise')) building = c.long_name;
		if (c.types.includes('premise')) streetNo = c.long_name;
		if (c.types.includes('administrative_area_level_1')) prefecture = c.long_name;
	});
	return {
		address: result.formatted_address,
		geo: latLng,
		zipCode,
		building,
		streetNo,
		prefecture,
	};
};

export const PlacesAutocomplete = ({setSelected}: {setSelected: (location?: SelectedLocation) => void}) => {
	const {
		value,
		suggestions: {status, data},
		setValue,
		clearSuggestions,
	} = usePlacesAutocomplete({
		requestOptions: {},
		debounce: 300,
	});

	const {i18n} = useI18n();

	const handleSelect = (placeId: string) => {
		clearSuggestions();
		getGeocode({placeId}).then((results) => {
			const location = buildLocationFromResult(results[0]);
			setSelected(location);
			setValue(location.address, false);
		});
	};

	const removeSelection = () => {
		clearSuggestions();
		setSelected(undefined);
	};

	return (
		<div className='inline-flex flex-col justify-center relative text-gray-500'>
			<Combobox value={value} onChange={handleSelect}>
				<div className='relative mt-1'>
					<div className='relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left sm:shadow-md focus:outline-none focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm flex'>
						<div className='py-2 px-3'>
							<SearchIcon className='[&>path]:stroke-black [&>path]:opacity-40' />
						</div>
						<Combobox.Input
							className='w-full border-none py-2 pr-10 text-sm text-gray-900 focus-visible:outline-none'
							placeholder={i18n('AUTH.PROFILE.MAP.FIELD.SEARCH.PLACEHOLDER')}
							onChange={(e) => setValue(e.target.value)}
						/>
						<Combobox.Button className='absolute inset-y-0 right-0 flex items-center pr-2' onClick={removeSelection}>
							<CrossIcon className='[&>path]:stroke-black [&>path]:opacity-40' />
						</Combobox.Button>
					</div>
					{status === 'OK' && (
						<Combobox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
							{data.map(({place_id, structured_formatting: {main_text, secondary_text}}) => {
								return (
									<Combobox.Option
										key={place_id}
										className='pl-8 pr-2 py-1 border-gray-100 relative cursor-pointer hover:bg-yellow-50 hover:text-gray-900'
										value={place_id}
									>
										<strong>{main_text}</strong> <small>{secondary_text}</small>
									</Combobox.Option>
								);
							})}
						</Combobox.Options>
					)}
				</div>
			</Combobox>
		</div>
	);
};
